/**
 * AUTHENTICATION API SERVICE
 * Handles user authentication and authorization.
 * Payload shapes follow Back_End_Auth_System src/controllers/authController.js.
 */

import axiosInstance from './axios.config';
import { API_ENDPOINTS } from '@/config/api.config';
import type {
  AuthUserPayload,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  MeResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
} from '@/types/api.types';
import type { User } from '@/types/auth.types';

/** The auth service's user (Mongo `_id`, no permissions) as the app keeps it. */
export function sessionUserFrom(p: AuthUserPayload): User {
  const lastLogin = p.lastLogin ? Date.parse(p.lastLogin) : NaN;
  return {
    id: p._id,
    username: p.username || [p.firstName, p.lastName].filter(Boolean).join(' ') || p.organizationName || p.email || '',
    email: p.email || p.organizationEmail || '',
    role: p.role || 'user',
    permissions: [],
    lastLogin: Number.isFinite(lastLogin) ? lastLogin : undefined,
    twoFactorEnabled: p.twoFactorEnabled,
    accountStatus: p.accountStatus,
  };
}

export const authApi = {
  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message?: string }> {
    // login-only system: password changes happen HERE, no registration exists
    const response = await axiosInstance.patch(API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
      { currentPassword, newPassword });
    return response.data;
  },

  /**
   * Login user
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await axiosInstance.post<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, credentials);
    return response.data;
  },

  /**
   * Logout user
   */
  async logout(): Promise<LogoutResponse> {
    const response = await axiosInstance.post<LogoutResponse>(API_ENDPOINTS.AUTH.LOGOUT);
    return response.data;
  },

  /**
   * Refresh access token
   */
  async refreshToken(request: RefreshTokenRequest): Promise<RefreshTokenResponse> {
    const response = await axiosInstance.post<RefreshTokenResponse>(API_ENDPOINTS.AUTH.REFRESH, request);
    return response.data;
  },

  /**
   * Get current user (the /me profile)
   */
  async getCurrentUser(): Promise<MeResponse> {
    const response = await axiosInstance.get<MeResponse>(API_ENDPOINTS.AUTH.ME);
    return response.data;
  },
};
